/**
 * 小獅船長 — KIOSK 吉祥物（對應 docs/Kiosk_UI_Flow.md C-2）。
 *
 * 素材為業主提供之正式 IP，原始檔見 docs/ITRI製作小獅船長動態檔 與
 * docs/福爾摩沙酒店小獅船長IP；public/mascot/ 下為機台用壓縮版本：
 *   - 動態 mascot-{zh,en,ja}.{webm,mp4} — ITRI landing 動畫，540×960 靜音（原檔 1080×1920 含音軌）
 *   - 靜態 lion-*.png — 去背立繪 6 種表情，360×540（原檔 1664×2496）
 *
 * 原始素材為純黑背景，轉檔時以 colorkey 去黑，讓小獅直接融進頁面底色、不需外框：
 *   - webm (VP9 yuva420p) 保留 alpha，背景即頁面底色本身，零色差 —— Chromium 走這支
 *   - mp4 無 alpha 通道，退而將底色 --c-bg (#f8fafb) 烤進畫面，供不支援 VP9 alpha 的
 *     瀏覽器 fallback（有損壓縮使角色周邊背景約有 1–2/255 色差，肉眼不可辨）
 * ⚠ 若日後調整 --c-bg，mp4 須一併重轉，否則 fallback 會出現色塊邊界；webm 不受影響。
 *
 * 影片一律 muted + playsInline，否則機台瀏覽器會擋自動播放。
 */

import type { Locale } from '../lib/i18n';
import { useKiosk } from '../lib/session';

/** IP 立繪表情；'video' 為依語系播放的 landing 動畫。 */
export type MascotPose = 'idle' | 'smile' | 'wink' | 'wave' | 'wave-smile' | 'cheer' | 'video';

const PRIMARY = 'var(--c-primary)';

/** 動畫檔名的語系代碼（素材沿用 ITRI 命名，非 Locale 字面值）。 */
const VIDEO_CODE: Record<Locale, string> = {
  'zh-TW': 'zh',
  en: 'en',
  ja: 'ja',
};

/** 影片載入前的墊圖，避免閒置畫面閃黑框。 */
const POSTER = '/mascot/lion-idle.png';

interface MascotProps {
  /** 顯示寬度 px；高度依素材比例換算（影片 9:16、立繪 2:3）。 */
  size?: number;
  label?: string;
  pose?: MascotPose;
}

export function Mascot({ size = 220, label, pose = 'idle' }: MascotProps): JSX.Element {
  const { locale } = useKiosk();

  return (
    <div style={{ textAlign: 'center' }}>
      {pose === 'video' ? (
        <video
          // 語系切換時重新掛載，才會換成對應語言的動畫。
          key={locale}
          poster={POSTER}
          autoPlay
          loop
          muted
          playsInline
          role="img"
          aria-label="小獅船長"
          style={{
            display: 'block',
            margin: '0 auto',
            width: size,
            height: Math.round((size * 16) / 9),
            objectFit: 'cover',
          }}
        >
          {/* webm 在前：支援 alpha 的瀏覽器優先取用，其餘退到烤底色的 mp4。 */}
          <source src={`/mascot/mascot-${VIDEO_CODE[locale]}.webm`} type="video/webm" />
          <source src={`/mascot/mascot-${VIDEO_CODE[locale]}.mp4`} type="video/mp4" />
        </video>
      ) : (
        <img
          src={`/mascot/lion-${pose}.png`}
          alt="小獅船長"
          style={{ display: 'block', margin: '0 auto', width: size, height: 'auto' }}
        />
      )}
      {label !== undefined && (
        <div style={{ fontSize: 'var(--fs-small)', fontWeight: 700, color: PRIMARY, marginTop: 4 }}>
          {label}
        </div>
      )}
    </div>
  );
}
